import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Save, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const SystemSettingsTab = () => {
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Green Door',
    smtp_use_tls: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  const fetchEmailConfig = async () => {
    try {
      const response = await api.get('/system-settings/email');
      if (response.data) {
        const hasExistingConfig = !!response.data.smtp_host;
        setEmailConfig({
          smtp_host: response.data.smtp_host || '',
          smtp_port: response.data.smtp_port || '587',
          smtp_username: response.data.smtp_username || '',
          smtp_password: '', // Don't show password
          smtp_from_email: response.data.smtp_from_email || '',
          smtp_from_name: response.data.smtp_from_name || 'Green Door',
          smtp_use_tls: response.data.smtp_use_tls !== false,
          _hasExistingPassword: hasExistingConfig,
        });
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
      // Config might not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!emailConfig.smtp_host || !emailConfig.smtp_username || !emailConfig.smtp_from_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await api.post('/system-settings/email', emailConfig);
      toast.success('Email server settings saved');
    } catch (error) {
      console.error('Error saving email config:', error);
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      await api.post('/system-settings/email/test', {
        to_email: emailConfig.smtp_from_email
      });
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>System Email Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Configure the email server used to send system-generated emails and alerts.
            </p>

            {/* SMTP Host */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SMTP Host *</Label>
                <Input
                  value={emailConfig.smtp_host}
                  onChange={(e) => setEmailConfig({ ...emailConfig, smtp_host: e.target.value })}
                  placeholder="smtp.gmail.com"
                />
              </div>

              <div className="space-y-2">
                <Label>SMTP Port *</Label>
                <Input
                  type="number"
                  value={emailConfig.smtp_port}
                  onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: e.target.value })}
                  placeholder="587"
                />
              </div>
            </div>

            {/* SMTP Username */}
            <div className="space-y-2">
              <Label>Email Username *</Label>
              <Input
                type="email"
                value={emailConfig.smtp_username}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtp_username: e.target.value })}
                placeholder="mailbox@mygreendoorapp.com"
              />
            </div>

            {/* SMTP Password */}
            <div className="space-y-2">
              <Label>Email Password *</Label>
              <Input
                type="password"
                value={emailConfig.smtp_password}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })}
                placeholder={emailConfig._hasExistingPassword ? "••••••••  (saved — leave blank to keep)" : "Enter password..."}
              />
              <p className="text-xs text-muted-foreground">
                For Gmail, use an App Password. For Outlook, use your account password.
              </p>
            </div>

            {/* From Email */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Email *</Label>
                <Input
                  type="email"
                  value={emailConfig.smtp_from_email}
                  onChange={(e) => setEmailConfig({ ...emailConfig, smtp_from_email: e.target.value })}
                  placeholder="mailbox@mygreendoorapp.com"
                />
              </div>

              <div className="space-y-2">
                <Label>From Name</Label>
                <Input
                  value={emailConfig.smtp_from_name}
                  onChange={(e) => setEmailConfig({ ...emailConfig, smtp_from_name: e.target.value })}
                  placeholder="Green Door"
                />
              </div>
            </div>

            {/* TLS */}
            <div className="space-y-2">
              <Label>Connection Security</Label>
              <Select 
                value={emailConfig.smtp_use_tls ? 'tls' : 'none'} 
                onValueChange={(value) => setEmailConfig({ ...emailConfig, smtp_use_tls: value === 'tls' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tls">TLS/STARTTLS (Recommended)</SelectItem>
                  <SelectItem value="none">None (Not Recommended)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Common Providers Help */}
            <div className="p-4 bg-muted/50 rounded text-xs space-y-1">
              <p className="font-semibold">Common Providers:</p>
              <p>• Gmail: smtp.gmail.com:587 (Use App Password)</p>
              <p>• Outlook: smtp-mail.outlook.com:587</p>
              <p>• Custom Domain: smtp.yourdomain.com:587</p>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-4 border-t flex gap-3">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Configuration</>
              )}
            </Button>
            <Button variant="outline" onClick={handleTestEmail} disabled={testing || !emailConfig.smtp_host}>
              {testing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Mail className="w-4 h-4 mr-2" /> Send Test Email</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemSettingsTab;
